import getControlsFromWorkflow from '../../Shared/jsonSchemaUtilities/getControlsFromWorkflow';
import { InputTextField } from '@abbott-components/core';
import { JSONSchema7 } from 'json-schema';
import { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { WorkflowEngineContext } from '../../WorkflowEngine/workflowEngineReducer';
import ControlKind from '../../Shared/controlKind';
import ControlState from '../../Shared/controlState';
import generateCalculatedValue from './generateCalculatedValue';
import JSONSchema7Properties from '../../Shared/jSONSchema7Properties';
import PageState from '../../Shared/pageState';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import getWorkflowFlattenData from '../../Shared/getWorkflowFlattenData';

export default (control: ControlState) => {
  const { state } = useContext(WorkflowEngineContext);

  const { pages, workflowData } = state;

  const formContext = useFormContext();

  const fieldState = formContext?.getFieldState(control.id);

  const properties = control.schema?.properties as JSONSchema7Properties;

  const labelSchema = properties['label'] as JSONSchema7;
  const expressionSchema = properties['expression'] as JSONSchema7;

  const configuredExpression = expressionSchema?.default as string;

  const numericControlIds = getControlsFromWorkflow(
    pages,
    ControlKind.NUMERIC
  ).map((ctr) => ctr.id);

  const calculateValue = (
    value: string,
    pages: PageState[],
    data: Record<string, unknown>
  ): void => {
    formContext?.clearErrors();
    try {
      const calcValue = generateCalculatedValue(value, pages, data);
      const valueToSet = isNaN(calcValue) ? null : calcValue;
      formContext?.setValue(control.id, valueToSet);
    } catch (error) {
      formContext?.setValue(control.id, null);
      formContext?.setError(
        control.id,
        {
          type: 'custom',
          message: (error as Error).message || 'Calculation Error'
        },
        { shouldFocus: true }
      );
    }
  };

  useEffect(() => {
    // calculate on form changes
    const subscription = formContext?.watch((watchedData, { name }) => {
      if (numericControlIds.includes(name as string)) {
        calculateValue(configuredExpression, pages, {
          ...getWorkflowFlattenData(workflowData),
          ...watchedData
        });
      }
    });

    // calculate on load
    calculateValue(
      configuredExpression,
      pages,
      getWorkflowFlattenData(workflowData)
    );

    return () => subscription?.unsubscribe();
  }, []);

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h6">{labelSchema?.default}: </Typography>
        <Typography variant="h6">
          {formContext?.getValues(control.id)}
        </Typography>
      </Stack>
      {fieldState?.error?.message && (
        <Typography sx={{ marginTop: 2, color: 'abbott.red' }}>
          {fieldState?.error?.message}
        </Typography>
      )}
      <InputTextField sx={{ display: 'none' }} name={control.id} />
    </Stack>
  );
};
